import { useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useSites } from "@/api/hooks/sites";
import {
  useCreateSiteAthenaMapping, useDeleteSiteAthenaMapping,
  useSiteAthenaMappings,
} from "@/api/hooks/siteAthenaMappings";
import { formatSiteDisplayId } from "@/api/labels";

const DOMAINS = [
  { value: "encounters", label: "Encounters" },
  { value: "notes", label: "Notes (unstructured)" },
] as const;

const DOMAIN_LABEL: Record<string, string> = Object.fromEntries(
  DOMAINS.map((d) => [d.value, d.label]),
);

/** Per-(site, domain) Athena query overrides. The data-delivery
 * refresh job uses these to issue ``SELECT max(<date_column>) FROM
 * raw_<site>.<table_name>`` — sites without a row use sensible
 * defaults (``encounters.encounter_date`` for the encounters domain,
 * probed column names for ``notes``). */
export function AthenaMappingsPage() {
  const sitesQuery = useSites();
  const mappingsQuery = useSiteAthenaMappings();
  const create = useCreateSiteAthenaMapping();
  const remove = useDeleteSiteAthenaMapping();

  const [siteId, setSiteId] = useState("");
  const [domain, setDomain] = useState<string>("encounters");
  const [tableName, setTableName] = useState("encounters");
  const [dateColumn, setDateColumn] = useState("encounter_date");
  const [notes, setNotes] = useState("");

  const sites = sitesQuery.data ?? [];
  const mappings = mappingsQuery.data ?? [];
  const siteById = useMemo(
    () => new Map(sites.map((s) => [s.id, s])),
    [sites],
  );

  const onDomainChange = (next: string) => {
    setDomain(next);
    // Sensible defaults so the admin only needs to override what's
    // actually different at their site.
    if (next === "encounters") {
      setTableName("encounters");
      setDateColumn("encounter_date");
    } else if (next === "notes") {
      setTableName("notes");
      setDateColumn("note_date");
    }
  };

  const canSubmit =
    Boolean(siteId) &&
    Boolean(domain) &&
    Boolean(tableName.trim()) &&
    Boolean(dateColumn.trim()) &&
    !create.isPending;

  const onCreate = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        site_id: siteId,
        domain,
        table_name: tableName.trim(),
        date_column: dateColumn.trim(),
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          setSiteId("");
          setNotes("");
        },
      },
    );
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Athena mappings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Override the table and date column the data-delivery refresh
        job queries for a specific site. Sites without an entry use
        the defaults (encounters.encounter_date for structured data,
        probed columns for notes).
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "center" }, mb: 3, flexWrap: "wrap" }}
      >
        <TextField
          select size="small" label="Site" value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          sx={{ minWidth: 260 }}
        >
          {sites.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {formatSiteDisplayId(s.display_id)} · {s.common_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Domain" value={domain}
          onChange={(e) => onDomainChange(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {DOMAINS.map((d) => (
            <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="Table" value={tableName}
          onChange={(e) => setTableName(e.target.value)}
          sx={{ minWidth: 180 }}
        />
        <TextField
          size="small" label="Date column" value={dateColumn}
          onChange={(e) => setDateColumn(e.target.value)}
          sx={{ minWidth: 180 }}
        />
        <TextField
          size="small" label="Notes (optional)" value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={onCreate}
        >
          {create.isPending ? "Adding…" : "Add mapping"}
        </Button>
      </Stack>

      {create.error && (
        <Alert severity="error" sx={{ mb: 2 }}>{create.error.message}</Alert>
      )}

      {mappingsQuery.isLoading || sitesQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : mappings.length === 0 ? (
        <Typography color="text.secondary">
          No mappings yet — every site is using the default Athena
          query layout.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Site</TableCell>
              <TableCell sx={{ width: 180 }}>Domain</TableCell>
              <TableCell sx={{ width: 160 }}>Table</TableCell>
              <TableCell sx={{ width: 200 }}>Date column</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right" sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((m) => {
              const site = siteById.get(m.site_id);
              return (
                <TableRow key={m.id} hover>
                  <TableCell>
                    {site
                      ? `${formatSiteDisplayId(site.display_id)} · ${site.common_name}`
                      : m.site_id.slice(0, 8)}
                  </TableCell>
                  <TableCell>{DOMAIN_LABEL[m.domain] ?? m.domain}</TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {m.table_name}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {m.date_column}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {m.notes ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (window.confirm("Delete this mapping?")) {
                            remove.mutate(m.id);
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
